// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ChargeStatusColorMap,
  ChargeStatusLabelMap,
  ChargeStatuses,
} from '@/constants/charge-status';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { ProjectBillingChart } from '@/project/components/project-billing-chart';
import { CurrencyBillingDatum } from '@/project/hooks/use-project-billing-aggregate';
import { Box, Grid, Paper, Stack, Typography, useTheme } from '@mui/material';

interface Props {
  datum: CurrencyBillingDatum;
}

// Bar heights are plotted as numbers; the displayed amounts stay Decimal.
function toChartData(datum: CurrencyBillingDatum) {
  return {
    totalAmount: datum.totalAmount.toNumber(),
    totalEvents: datum.totalEvents,
    amountsByStatus: {
      PLANNED: datum.amountsByStatus.PLANNED.toNumber(),
      BILLABLE: datum.amountsByStatus.BILLABLE.toNumber(),
      INVOICED: datum.amountsByStatus.INVOICED.toNumber(),
      CANCELLED: datum.amountsByStatus.CANCELLED.toNumber(),
    },
  };
}

/**
 * Renders the billing summary for a single currency: a mini chart with the
 * currency total, then a per-status breakdown grid. Amounts are formatted with
 * the section's own ISO code.
 */
export function ProjectBillingCurrencySection(props: Props) {
  const theme = useTheme();
  const { datum } = props;

  return (
    <>
      <Box padding={2} paddingBottom={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <ProjectBillingChart
              projects={[]}
              customData={toChartData(datum)}
              currencyCode={datum.isoCode}
              variant="mini"
            />
          </Box>
          <Typography
            variant="h4"
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            paddingRight={0.5}
          >
            {formatCurrencyValue({
              amount: datum.totalAmount,
              isoCode: datum.isoCode,
            })}
          </Typography>
        </Stack>
      </Box>

      <Grid container spacing={2} padding={2} paddingTop={1}>
        {ChargeStatuses.map((status) => (
          <Grid item xs={12} sm={6} md={3} key={status}>
            <Paper variant="outlined" sx={{ px: 2.5, py: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: ChargeStatusColorMap(status, theme),
                  }}
                />
                <Typography variant="subtitle2">
                  {ChargeStatusLabelMap(status)}
                </Typography>
              </Stack>
              <Typography variant="h6">
                {formatCurrencyValue({
                  amount: datum.amountsByStatus[status],
                  isoCode: datum.isoCode,
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {datum.totalAmount.isZero()
                  ? '0'
                  : datum.amountsByStatus[status]
                      .dividedBy(datum.totalAmount)
                      .times(100)
                      .toFixed(0)}
                % of total
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </>
  );
}
